import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import { getIndividuals } from 'api';
import { getPersonalRecords, getTrackEvents } from 'api/TrackEvents';
import {
    AvatarWithInitials,
    CustomButton,
    LayoutWithTopBar,
    ListWithSearchContainer,
} from 'components';
import debounce from 'lodash/debounce';
import IndividualCard from 'modules/community/components/IndividualCard';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ROUTES from 'Routes/routes';
import {
    Individual,
    Meta,
    PaginationLink,
    useToggle,
    formatTrackEventResult,
    normalizeTrackEventName,
} from 'utils';
import {
    AssignManagingUserDialog,
    CreateIndividualModal,
    IndividualsListSkeleton,
} from '../components';
import IndividualInviteDialog from '../components/IndividualInviteDialog';
import TrackEventViewer from '../components/TrackEventViewer';
import { assignManagingUserToIndividual, AssignManagingUserPayload } from '../api/individuals.api';
import { useSelector } from 'react-redux';
import {
    primaryOrganizationUuidSelector,
    primarySportSelector,
    getOrganizationsWithOrgAdminRole,
} from '../../../redux/reducers/user';

export default function ListOfIndividuals(): ReactElement {
    const { goBack, push } = useHistory();
    const theme = useTheme();
    const primaryOrganizationUuid = useSelector(primaryOrganizationUuidSelector);
    const primarySport = useSelector(primarySportSelector);
    const orgAdminOrgs = useSelector(getOrganizationsWithOrgAdminRole);
    const isOrgAdmin = (orgAdminOrgs?.length ?? 0) > 0;
    const isTrackFeaturesVisible = primarySport === 'track' || primarySport === 'multi_sport';

    const initialUrlSearchParams = new URLSearchParams(window.location.search);
    const initialOrgFilter =
        initialUrlSearchParams.get('filter[organization]') || primaryOrganizationUuid || undefined;
    const [query, setQuery] = useState<{
        'filter[name]'?: string;
        'filter[organization]'?: string;
        'filter[has_managing_user]'?: boolean;
        sort?: string;
        page?: number;
        limit?: number;
    }>({
        'filter[organization]': initialOrgFilter,
        page: 1,
        limit: 100,
        sort: 'name',
    });

    const [searchValue, setSearchValue] = useState<string>(query['filter[name]'] as string);
    const [individuals, setIndividuals] = useState<Individual[]>([]);
    const [selectedIndividual, setSelectedIndividual] = useState<Individual | null>(null);
    const [assignUserIndividual, setAssignUserIndividual] = useState<Individual | null>(null);
    const [assigningUser, setAssigningUser] = useState(false);
    const [meta, setMeta] = useState<Meta>();
    const [links, setLinks] = useState<PaginationLink>();
    const [loaded, setLoaded] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [createIndividualModal, toggleCreateIndividualModal] = useToggle(false);
    const [snackMessage, setSnackMessage] = useState<string>('');

    // Track PR view state
    const [trackViewMode, setTrackViewMode] = useState(false);
    const [rawPersonalRecords, setRawPersonalRecords] = useState<any[]>([]);
    const [personalRecords, setPersonalRecords] = useState<any[]>([]);
    const [loadingPRs, setLoadingPRs] = useState(false);

    // Event matching state
    const [customMappings] = useState<Record<string, string>>(() => {
        try {
            return JSON.parse(localStorage.getItem('track-event-custom-mappings') || '{}');
        } catch {
            return {};
        }
    });

    const resolveEventName = useCallback(
        (rawName: string): string => {
            if (customMappings[rawName]) return customMappings[rawName];
            return normalizeTrackEventName(rawName);
        },
        [customMappings],
    );

    // Individual viewer state (See All Meet Results)
    const [individualViewerOpen, setIndividualViewerOpen] = useState(false);
    const [individualViewerAthlete, setIndividualViewerAthlete] = useState<string>('');

    // Event detail drill-down state
    const [eventDetailOpen, setEventDetailOpen] = useState(false);
    const [eventDetailEvent, setEventDetailEvent] = useState('');
    const [eventDetailPR, setEventDetailPR] = useState<any>(null);
    const [eventDetailData, setEventDetailData] = useState<any[]>([]);
    const [eventDetailLoading, setEventDetailLoading] = useState(false);
    const [eventDetailAthleteName, setEventDetailAthleteName] = useState('');

    const handleDialogClose = () => {
        setSelectedIndividual(null);
    };

    const handleSearchValueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value);
        debounceLoadData({ 'filter[name]': event.target.value, page: 1 });
    };

    const onNextClicked = () => {
        const nextPage = meta ? meta.current_page + 1 : 1;
        setQuery((query) => ({ ...query, page: nextPage }));
    };

    const onPrevClicked = () => {
        const prevPage = meta ? meta.current_page - 1 : 1;
        setQuery((query) => ({ ...query, page: prevPage }));
    };

    const handleIndividualClicked = (individual: Individual) => {
        push(ROUTES.IndividualDetails.path.replace(':individualId', individual.uuid));
    };

    const handleInviteClicked = (individual: Individual) => {
        setSelectedIndividual(individual);
    };

    const handleAssignUserClicked = (individual: Individual) => {
        setAssignUserIndividual(individual);
    };

    const handleAssignManagingUser = async (payload: AssignManagingUserPayload) => {
        if (!assignUserIndividual) return;
        setAssigningUser(true);
        try {
            const response = await assignManagingUserToIndividual(
                assignUserIndividual.uuid,
                payload,
            );
            setIndividuals((prev) =>
                prev.map((ind) =>
                    ind.uuid === assignUserIndividual.uuid ? response.data.individual : ind,
                ),
            );
            setAssignUserIndividual(null);
            setSnackMessage(response.data.message || 'Managing user assigned successfully.');
        } catch (error: any) {
            let errorMsg = 'Failed to assign managing user.';
            if (error?.response?.data?.message) {
                errorMsg = error.response.data.message;
            } else if (error?.response?.data?.errors) {
                errorMsg = Object.values(error.response.data.errors).flat().join(', ');
            }
            setSnackMessage(errorMsg);
        } finally {
            setAssigningUser(false);
        }
    };

    const debounceLoadData = useCallback(debounce(setQuery, 400), []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setLoading(true);
        if (!query) {
            return;
        }
        getIndividuals(query)
            .then((response) => {
                setLoaded(true);
                setIndividuals(response.data.data);
                setMeta(response.data.meta);
                setLinks(response.data.links);
            })
            .finally(() => {
                setLoading(false);
            });

        return () => {};
    }, [query]);

    // Fetch PRs when track view mode is enabled and we have individuals
    useEffect(() => {
        const fetchPRs = async () => {
            if (!trackViewMode || individuals.length === 0) return;

            setLoadingPRs(true);
            try {
                const individualUuids = individuals.map((ind) => ind.uuid);
                const response = await getPersonalRecords({
                    'filter.individual': individualUuids,
                });
                setRawPersonalRecords(response.data.personal_records || []);
            } catch (error) {
                console.error('Failed to fetch personal records:', error);
                setRawPersonalRecords([]);
            } finally {
                setLoadingPRs(false);
            }
        };

        fetchPRs();
    }, [trackViewMode, individuals]);

    // Merge PRs using current mappings
    useEffect(() => {
        if (rawPersonalRecords.length === 0) {
            setPersonalRecords([]);
            return;
        }

        const mergedMap = new Map<string, any>();
        const toIn = (v: number, u: string) => (u === 'm' ? v * 39.3701 : v);

        for (const pr of rawPersonalRecords) {
            const normalized = resolveEventName(pr.event_type);
            const key = `${pr.individual_uuid}|${normalized}`;
            const existing = mergedMap.get(key);
            const normUnit = pr.result_unit === 'm' ? 'in' : pr.result_unit;
            const normResult =
                pr.result_unit === 'm'
                    ? (parseFloat(pr.best_result) * 39.3701).toFixed(2)
                    : pr.best_result;

            if (!existing) {
                mergedMap.set(key, {
                    ...pr,
                    event_type: normalized,
                    best_result: normResult,
                    result_unit: normUnit,
                    event_count: pr.event_count || 1,
                });
            } else {
                const isTimeBased = existing.result_unit === 's';
                const curr = toIn(parseFloat(existing.best_result), existing.result_unit);
                const next = toIn(parseFloat(pr.best_result), pr.result_unit);
                const isBetter = isTimeBased ? next < curr : next > curr;
                const mergedCount = Math.max(existing.event_count || 1, pr.event_count || 1);
                if (isBetter) {
                    mergedMap.set(key, {
                        ...pr,
                        event_type: normalized,
                        best_result: normResult,
                        result_unit: normUnit,
                        event_count: mergedCount,
                    });
                } else {
                    existing.event_count = mergedCount;
                }
            }
        }

        setPersonalRecords(Array.from(mergedMap.values()));
    }, [rawPersonalRecords, resolveEventName]);

    // PR tile drill-down handler
    const handlePRTileClick = async (pr: any, athleteName: string) => {
        setEventDetailEvent(pr.event_type);
        setEventDetailPR(pr);
        setEventDetailAthleteName(athleteName);
        setEventDetailOpen(true);
        setEventDetailLoading(true);
        setEventDetailData([]);

        try {
            let allResults: any[] = [];
            let currentPage = 1;
            let hasMore = true;

            while (hasMore) {
                const response = await getTrackEvents({
                    'filter.individual': pr.individual_uuid,
                    limit: 500,
                    page: currentPage,
                });
                allResults = [...allResults, ...response.data.data];
                hasMore = response.data.meta?.current_page < response.data.meta?.last_page;
                currentPage++;
            }

            const eventType = resolveEventName(pr.event_type);
            const filtered = allResults.filter(
                (perf: any) => resolveEventName(perf.event_type) === eventType,
            );

            const seen = new Set<string>();
            const deduped = filtered.filter((perf: any) => {
                const val =
                    perf.result_unit === 'm'
                        ? (parseFloat(perf.result_value) * 39.3701).toFixed(2)
                        : parseFloat(perf.result_value).toFixed(2);
                const d = perf.meet_date.split('T')[0];
                const key = `${d}|${val}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });

            deduped.sort(
                (a: any, b: any) =>
                    new Date(b.meet_date).getTime() - new Date(a.meet_date).getTime(),
            );
            setEventDetailData(deduped);
        } catch (error) {
            console.error('Failed to fetch event details:', error);
            setEventDetailData([]);
        } finally {
            setEventDetailLoading(false);
        }
    };

    const isPRRow = (perf: any) => {
        if (!eventDetailPR) return false;
        const perfVal =
            perf.result_unit === 'm'
                ? parseFloat(perf.result_value) * 39.3701
                : parseFloat(perf.result_value);
        const prVal = parseFloat(eventDetailPR.best_result);
        return Math.abs(perfVal - prVal) < 0.1 && perf.meet_date === eventDetailPR.meet_date;
    };

    const getSourceLabel = (source: string | null) => {
        if (!source) return 'Manual';
        if (source.toLowerCase().includes('athletic')) return 'Athletic.net';
        if (source.toLowerCase().includes('mile')) return 'MileSplit';
        return source;
    };

    const getSourceColor = (source: string | null): 'primary' | 'secondary' | 'default' => {
        if (!source) return 'default';
        if (source.toLowerCase().includes('athletic')) return 'primary';
        if (source.toLowerCase().includes('mile')) return 'secondary';
        return 'default';
    };

    return (
        <React.Fragment>
            <Snackbar
                open={snackMessage !== ''}
                message={snackMessage}
                autoHideDuration={6000}
                onClose={() => {
                    setSnackMessage('');
                }}
            />
            <LayoutWithTopBar title="Individuals" onBack={goBack}>
                <Container
                    maxWidth={false}
                    sx={{
                        width: { xs: '100%', md: '90vw' },
                        maxWidth: '100%',
                        px: { xs: 1, md: 3 },
                    }}
                >
                    <ListWithSearchContainer
                        searchValue={searchValue}
                        setSearchValue={handleSearchValueChanged}
                        setOpen={() => {}}
                        onClickButton={toggleCreateIndividualModal}
                        isError={false}
                        errorMessage=""
                        searchBarPlaceHolder="Search"
                        buttonText="Add Individual"
                        searchBarLoader={false}
                        onFilter={(q) => {
                            setQuery((query) => ({
                                ...query,
                                ...q,
                            }));
                        }}
                        enabledFilters={{
                            'filter[organization]': query['filter[organization]'] || '',
                        }}
                    >
                        <Box
                            sx={{
                                marginBottom: 6,
                                marginTop: 5,
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                justifyContent: 'space-between',
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Button
                                    variant={
                                        !query['filter[has_managing_user]']
                                            ? 'outlined'
                                            : 'contained'
                                    }
                                    onClick={() => {
                                        let newQuery = { ...query };
                                        if (query['filter[has_managing_user]']) {
                                            newQuery['filter[has_managing_user]'] = undefined;
                                        } else {
                                            newQuery['filter[has_managing_user]'] = true;
                                        }
                                        setQuery(newQuery);
                                    }}
                                >
                                    {!query['filter[has_managing_user]'] ? (
                                        <VerifiedUserIcon sx={{ color: '#1B9CEA' }} />
                                    ) : (
                                        ''
                                    )}

                                    {query['filter[has_managing_user]']
                                        ? 'See All Athletes'
                                        : 'See Only Users'}
                                </Button>
                                {isTrackFeaturesVisible && (
                                    <Button
                                        variant={trackViewMode ? 'contained' : 'outlined'}
                                        size="small"
                                        onClick={() => setTrackViewMode(!trackViewMode)}
                                        sx={{
                                            textTransform: 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        See Meet PRs
                                        <Switch
                                            size="small"
                                            checked={trackViewMode}
                                            onChange={() => setTrackViewMode(!trackViewMode)}
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{ ml: 0.5 }}
                                        />
                                    </Button>
                                )}
                            </Box>
                            <FormControl>
                                <InputLabel id="sort">Sort By</InputLabel>
                                <Select
                                    sx={{ minWidth: 200 }}
                                    labelId="sort"
                                    id="sort"
                                    value={query.sort}
                                    label="Sort By"
                                    onChange={(event) => {
                                        setQuery({
                                            ...query,
                                            sort: event.target.value,
                                            page: 1,
                                        });
                                    }}
                                    defaultValue={'name'}
                                >
                                    <MenuItem value={'name'}>A to Z</MenuItem>
                                    <MenuItem value={'-name'}>Z to A</MenuItem>
                                    <MenuItem value={'-created_at'}>Most Recently Added</MenuItem>
                                    <MenuItem value={'created_at'}>First Created</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        {!loaded && loading && <IndividualsListSkeleton />}
                        {loaded && individuals.length === 0 && (
                            <Box display="flex" justifyContent="center">
                                <Typography>No Individuals Found</Typography>
                            </Box>
                        )}

                        {/* PR Tiles View */}
                        {trackViewMode && isTrackFeaturesVisible ? (
                            loadingPRs ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : personalRecords.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography color="text.secondary">
                                        No personal records found for the current individuals.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            md: '1fr 1fr',
                                        },
                                        gap: 3,
                                    }}
                                >
                                    {individuals.map((individual) => {
                                        const individualPRs = personalRecords.filter(
                                            (pr) => pr.individual_uuid === individual.uuid,
                                        );

                                        if (individualPRs.length === 0) return null;

                                        return (
                                            <Box
                                                key={individual.uuid}
                                                sx={{
                                                    background: alpha(
                                                        theme.palette.background.paper,
                                                        0.6,
                                                    ),
                                                    backdropFilter: 'blur(20px)',
                                                    borderRadius: 2,
                                                    border: `1px solid ${alpha(
                                                        theme.palette.divider,
                                                        0.1,
                                                    )}`,
                                                    boxShadow: `0 4px 16px ${alpha(
                                                        theme.palette.common.black,
                                                        0.05,
                                                    )}`,
                                                    p: 2,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        mb: 2,
                                                    }}
                                                >
                                                    <AvatarWithInitials
                                                        src={
                                                            individual.image_urls?.avatar ||
                                                            individual.profile_photo ||
                                                            undefined
                                                        }
                                                        sx={{ width: 50, height: 50 }}
                                                        firstname={
                                                            individual.name?.split(' ')[0] ?? ''
                                                        }
                                                        lastname={
                                                            individual.name
                                                                ?.split(' ')
                                                                .slice(1)
                                                                .join(' ') ?? ''
                                                        }
                                                    />
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight="700"
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={() =>
                                                            handleIndividualClicked(individual)
                                                        }
                                                    >
                                                        {individual.name}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns:
                                                            'repeat(auto-fill, minmax(140px, 1fr))',
                                                        gap: 1.5,
                                                    }}
                                                >
                                                    {individualPRs.map((pr, prIndex) => (
                                                        <Box
                                                            key={prIndex}
                                                            onClick={() =>
                                                                handlePRTileClick(
                                                                    pr,
                                                                    individual.name,
                                                                )
                                                            }
                                                            sx={{
                                                                cursor: 'pointer',
                                                                background: alpha(
                                                                    theme.palette.background.paper,
                                                                    0.4,
                                                                ),
                                                                backdropFilter: 'blur(20px)',
                                                                borderRadius: 2,
                                                                border: `1px solid ${alpha(
                                                                    theme.palette.info.main,
                                                                    0.3,
                                                                )}`,
                                                                boxShadow: `0 0 10px ${alpha(
                                                                    theme.palette.info.main,
                                                                    0.15,
                                                                )}, 0 4px 16px ${alpha(
                                                                    theme.palette.common.black,
                                                                    0.05,
                                                                )}`,
                                                                p: 1.5,
                                                                transition:
                                                                    'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                                                                '&:hover': {
                                                                    transform: 'translateY(-2px)',
                                                                    borderColor: alpha(
                                                                        theme.palette.info.main,
                                                                        0.5,
                                                                    ),
                                                                    boxShadow: `0 0 15px ${alpha(
                                                                        theme.palette.info.main,
                                                                        0.3,
                                                                    )}, 0 6px 24px ${alpha(
                                                                        theme.palette.common.black,
                                                                        0.1,
                                                                    )}`,
                                                                },
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    mb: 1,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    fontWeight="600"
                                                                    sx={{ fontSize: '0.75rem' }}
                                                                >
                                                                    {pr.event_type}
                                                                </Typography>
                                                                {pr.event_count > 1 && (
                                                                    <Chip
                                                                        label={`${pr.event_count}`}
                                                                        size="small"
                                                                        color="info"
                                                                        variant="outlined"
                                                                        sx={{
                                                                            fontSize: 10,
                                                                            height: 20,
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                            <Typography
                                                                variant="h5"
                                                                fontWeight="bold"
                                                                sx={{
                                                                    color: theme.palette.info.main,
                                                                    mb: 0.5,
                                                                    letterSpacing: '-0.02em',
                                                                    fontSize: '1.5rem',
                                                                }}
                                                            >
                                                                {formatTrackEventResult(
                                                                    pr.best_result,
                                                                    pr.result_unit,
                                                                )}
                                                            </Typography>
                                                            {pr.meet_date && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    display="block"
                                                                    sx={{ fontSize: '0.7rem' }}
                                                                >
                                                                    {new Date(
                                                                        pr.meet_date,
                                                                    ).toLocaleDateString()}
                                                                </Typography>
                                                            )}
                                                            {pr.meet_name && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    display="block"
                                                                    sx={{
                                                                        mt: 0.25,
                                                                        fontWeight: 500,
                                                                        fontSize: '0.65rem',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {pr.meet_name}
                                                                </Typography>
                                                            )}
                                                            {pr.event_count > 1 &&
                                                                (() => {
                                                                    const n = pr.event_count;
                                                                    const border = `1px solid ${alpha(
                                                                        theme.palette.divider,
                                                                        0.15,
                                                                    )}`;
                                                                    const label = `See all ${n} meet time${
                                                                        n !== 1 ? 's' : ''
                                                                    }`;
                                                                    return (
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems:
                                                                                    'center',
                                                                                justifyContent:
                                                                                    'flex-end',
                                                                                mt: 1,
                                                                                pt: 0.75,
                                                                                borderTop: border,
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    color: theme
                                                                                        .palette
                                                                                        .info.main,
                                                                                    fontWeight: 600,
                                                                                    fontSize:
                                                                                        '0.6rem',
                                                                                }}
                                                                            >
                                                                                {label}
                                                                            </Typography>
                                                                        </Box>
                                                                    );
                                                                })()}
                                                        </Box>
                                                    ))}
                                                    <Box
                                                        onClick={() => {
                                                            setIndividualViewerAthlete(
                                                                individual.uuid,
                                                            );
                                                            setIndividualViewerOpen(true);
                                                        }}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            background: alpha(
                                                                theme.palette.background.paper,
                                                                0.25,
                                                            ),
                                                            backdropFilter: 'blur(24px)',
                                                            WebkitBackdropFilter: 'blur(24px)',
                                                            borderRadius: 2,
                                                            border: `1px solid ${alpha(
                                                                theme.palette.primary.main,
                                                                0.35,
                                                            )}`,
                                                            boxShadow: `0 0 12px ${alpha(
                                                                theme.palette.primary.main,
                                                                0.15,
                                                            )}, 0 4px 16px ${alpha(
                                                                theme.palette.common.black,
                                                                0.06,
                                                            )}`,
                                                            p: 1.5,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            minHeight: 100,
                                                            transition:
                                                                'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                                                            '&:hover': {
                                                                transform: 'translateY(-2px)',
                                                                borderColor: alpha(
                                                                    theme.palette.primary.main,
                                                                    0.6,
                                                                ),
                                                                boxShadow: `0 0 20px ${alpha(
                                                                    theme.palette.primary.main,
                                                                    0.3,
                                                                )}, 0 6px 24px ${alpha(
                                                                    theme.palette.common.black,
                                                                    0.1,
                                                                )}`,
                                                            },
                                                        }}
                                                    >
                                                        <EmojiEventsIcon
                                                            sx={{
                                                                fontSize: 28,
                                                                color: theme.palette.primary.main,
                                                                mb: 0.75,
                                                                opacity: 0.85,
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            fontWeight="700"
                                                            sx={{
                                                                color: theme.palette.primary.main,
                                                                fontSize: '0.75rem',
                                                                textAlign: 'center',
                                                                lineHeight: 1.3,
                                                            }}
                                                        >
                                                            See All Meet Results
                                                        </Typography>
                                                        <ChevronRightIcon
                                                            sx={{
                                                                fontSize: 16,
                                                                color: alpha(
                                                                    theme.palette.primary.main,
                                                                    0.6,
                                                                ),
                                                                mt: 0.5,
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )
                        ) : (
                            /* Normal Individual Cards View */
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: '1fr 1fr',
                                    },
                                    gap: 2,
                                }}
                            >
                                {individuals.map((individual) => (
                                    <IndividualCard
                                        key={individual.uuid}
                                        onInviteClicked={() => handleInviteClicked(individual)}
                                        onAssignUserClicked={() =>
                                            handleAssignUserClicked(individual)
                                        }
                                        onClick={() => handleIndividualClicked(individual)}
                                        individual={individual}
                                    />
                                ))}
                            </Box>
                        )}

                        <Box display="flex" flexDirection="row" justifyContent="space-between">
                            <CustomButton
                                variant="text"
                                color="primary"
                                disabled={!links?.prev}
                                startIcon={<NavigateBeforeIcon />}
                                onClick={() => onPrevClicked()}
                            >
                                Previous
                            </CustomButton>
                            <CustomButton
                                variant="text"
                                color="primary"
                                disabled={!links?.next}
                                endIcon={<NavigateNextIcon />}
                                onClick={() => onNextClicked()}
                            >
                                Next
                            </CustomButton>
                        </Box>
                    </ListWithSearchContainer>
                </Container>
            </LayoutWithTopBar>
            {selectedIndividual && (
                <IndividualInviteDialog
                    open={true}
                    onClose={handleDialogClose}
                    individual={selectedIndividual}
                />
            )}
            {assignUserIndividual && (
                <AssignManagingUserDialog
                    open={true}
                    onClose={() => setAssignUserIndividual(null)}
                    onSubmit={handleAssignManagingUser}
                    organizationUuid={assignUserIndividual.organization?.uuid || ''}
                    individualName={assignUserIndividual.name}
                    submitting={assigningUser}
                    defaultPrimarySport={primarySport}
                />
            )}
            <CreateIndividualModal
                onIndividualCreatedFailed={(message: string) => {
                    setSnackMessage(message);
                }}
                onIndividualCreated={(individual) => {
                    toggleCreateIndividualModal();
                    setIndividuals([...(individuals ?? []), individual]);
                }}
                onClose={toggleCreateIndividualModal}
                open={createIndividualModal}
            />

            {/* Event Detail Drill-Down Dialog */}
            <Dialog
                open={eventDetailOpen}
                onClose={() => setEventDetailOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        background: alpha(theme.palette.background.paper, 0.85),
                        backdropFilter: 'blur(20px)',
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pb: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton size="small" onClick={() => setEventDetailOpen(false)}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                {eventDetailEvent}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {eventDetailAthleteName} &middot; {eventDetailData.length}{' '}
                                performance
                                {eventDetailData.length !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                        {eventDetailPR && (
                            <Chip
                                icon={<EmojiEventsIcon />}
                                label={`PR: ${formatTrackEventResult(
                                    eventDetailPR.best_result,
                                    eventDetailPR.result_unit,
                                )}`}
                                color="success"
                                size="small"
                                sx={{ ml: 1, fontWeight: 600 }}
                            />
                        )}
                    </Box>
                    <IconButton onClick={() => setEventDetailOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {eventDetailLoading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : eventDetailData.length === 0 ? (
                        <Alert severity="info">No performances found for this event.</Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Meet</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            Result
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {eventDetailData.map((perf: any) => {
                                        const isHighlighted = isPRRow(perf);
                                        return (
                                            <TableRow
                                                key={perf.uuid}
                                                sx={{
                                                    ...(isHighlighted
                                                        ? {
                                                              borderLeft: `4px solid ${theme.palette.success.main}`,
                                                              background: alpha(
                                                                  theme.palette.success.main,
                                                                  0.08,
                                                              ),
                                                          }
                                                        : {}),
                                                    '&:hover': {
                                                        background: alpha(
                                                            theme.palette.action.hover,
                                                            0.04,
                                                        ),
                                                    },
                                                }}
                                            >
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        {isHighlighted && (
                                                            <EmojiEventsIcon
                                                                sx={{
                                                                    fontSize: 16,
                                                                    color: theme.palette.success
                                                                        .main,
                                                                }}
                                                            />
                                                        )}
                                                        <Typography variant="body2">
                                                            {new Date(
                                                                perf.meet_date,
                                                            ).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric',
                                                            })}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {perf.meet_name || '\u2014'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {perf.meet_location || '\u2014'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={isHighlighted ? 700 : 400}
                                                        sx={
                                                            isHighlighted
                                                                ? {
                                                                      color: theme.palette.success
                                                                          .main,
                                                                  }
                                                                : {}
                                                        }
                                                    >
                                                        {formatTrackEventResult(
                                                            perf.result_value,
                                                            perf.result_unit,
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {perf.import_source &&
                                                    perf.import_source
                                                        .toLowerCase()
                                                        .includes('mile') ? (
                                                        <img
                                                            src="/milesplit-logo.png"
                                                            alt="MileSplit"
                                                            style={{
                                                                height: 28,
                                                                objectFit: 'contain',
                                                            }}
                                                        />
                                                    ) : perf.import_source &&
                                                      perf.import_source
                                                          .toLowerCase()
                                                          .includes('athletic') ? (
                                                        <img
                                                            src="/athletic-net-logo.png"
                                                            alt="Athletic.net"
                                                            style={{
                                                                height: 28,
                                                                objectFit: 'contain',
                                                            }}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            label={getSourceLabel(
                                                                perf.import_source,
                                                            )}
                                                            color={getSourceColor(
                                                                perf.import_source,
                                                            )}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
            <TrackEventViewer
                open={individualViewerOpen}
                onClose={() => {
                    setIndividualViewerOpen(false);
                    setIndividualViewerAthlete('');
                }}
                group={null}
                initialAthlete={individualViewerAthlete}
                isOrgAdmin={isOrgAdmin}
                members={individuals.map((ind) => ({ individual: ind }))}
            />
        </React.Fragment>
    );
}
